import React, { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation, useQueryClient } from 'react-query'
import AvatarEditor from 'react-avatar-editor'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import ReactTooltip from 'react-tooltip'
import withApiData from '../HOC/withApiData'
import colors from '../../configuration/colors'
import { hideModal } from '../../actions/modal'
import Modal from '../mini/Modal'
import Button from '../mini/Buttons/Button'
import H2 from '../typography/headers/H2'
import MarginRight15 from '../typography/margins/MarginRight15'
import CancelButton from '../mini/Buttons/CancelButton'
import { removeActivityIcon, uploadImage } from '../mycourses/Activities/API'

const ModalContainer = styled.div`
  width: 660px;
  border-radius: 10px;
  box-shadow: 0 2px 3.8px 0.2px rgba(0, 0, 0, 0.15);
  background-color: ${colors.smoke};
  margin: 180px auto;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const HeaderContainer = styled.div`
  height: 80px;
  width: auto;
  border-radius: 10px 10px 0 0;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.rock};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
`

const MainContainer = styled.div`
  padding: 10px 30px 30px;
`

const HintText = styled.p`
  color: ${colors.aluminum};
  font-size: 11px;
  letter-spacing: 0;
  line-height: 16px;
`

const Label = styled.label`
  display: inline-block;
  border: 1px solid ${colors.aluminum};
  background: ${colors.white};
  color: ${colors.black};
  border-radius: 100px;
  padding: 12px 22px;
  margin-bottom: 10px;
  &:hover {
    color: ${colors.white};
    background: ${colors.charcoal2};
    border: 1px solid ${colors.charcoal2};
  }
`

const AddOrRemoveActivityIconModal = ({
  apiData,
  levelId,
  activityQuestId,
  activity
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [fileUrl, setFileUrl] = useState(
    activity.icon_image_url !== null ? activity.icon_image_url : null
  )
  const [fileName, setFileName] = useState(
    activity.icon_image_original_file_name !== null
      ? activity.icon_image_original_file_name
      : null
  )
  const [scale, setScale] = useState(1)
  const [editorState, setEditorState] = useState('')
  const [isHovered, setIsHovered] = useState(false)
  const [fileSize, setFileSize] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef(null)

  const setEditorRef = editor => {
    if (editor) {
      setEditorState(editor)
    }
  }

  const onFileLoad = e => {
    const reader = new FileReader()
    if (e.target.files[0].size < 500000) {
      const fileName = e.target.value.replace(/^.*[\\\/]/, '')
      reader.onload = e => {
        setFileName(fileName)
        setFileUrl(e.target.result)
      }
      setFileSize(e.target.files[0].size)

      reader.readAsDataURL(e.target.files[0])
    } else {
      setFileName(null)
      setFileUrl(null)
      setFileSize(e.target.files[0].size)
      document.getElementById('file-input').value = ''
    }
  }

  const onZoom = e => {
    setScale(e.target.value)
  }

  const closeTheModal = () => {
    setTimeout(() => {
      dispatch(hideModal())
    }, 300)
  }

  const createMutation = useMutation(
    ({ apiData, levelId, activityQuestId, type, fileUrl, fileName }) =>
      uploadImage(apiData, levelId, activityQuestId, type, fileUrl, fileName)
  )
  const couldSaveData = fileUrl !== null && fileSize < 500000

  const handleSaveActivityIcon = () => {
    const canvasScaled = editorState.getImageScaledToCanvas()
    const fileURL = canvasScaled.toDataURL()
    if (couldSaveData) {
      setIsLoading(true)
      createMutation.mutate(
        {
          apiData,
          levelId,
          activityQuestId,
          type: 'icon',
          fileUrl: fileURL,
          fileName
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('activity-' + activityQuestId)
            setIsLoading(false)
            setTimeout(() => {
              closeTheModal()
            }, 300)
          }
        }
      )
    }
  }

  const createMutationOnDeleteActivityIcon = useMutation(
    ({ apiData, levelId, activityQuestId, mentorFileId }) =>
      removeActivityIcon(apiData, levelId, activityQuestId, mentorFileId)
  )

  const handleDeleteActivityIcon = () => {
    createMutationOnDeleteActivityIcon.mutate(
      {
        apiData,
        levelId,
        activityQuestId
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('activity-' + activityQuestId)
          activity.icon_image_url = null
          setFileName(null)
        }
      }
    )
  }

  const displayAvatarCropper = () => {
    let iconComponent
    if (activity.icon_image_url === null && fileName !== null) {
      iconComponent = (
        <div>
          <AvatarEditor
            ref={setEditorRef}
            image={fileUrl}
            width={200}
            height={200}
            borderRadius={75}
            border={0}
            scale={scale}
          />
          <input
            className="zoom-slider"
            type="range"
            step="0.01"
            min="1"
            max="2"
            name="scale"
            defaultValue="1"
            onChange={onZoom}
            style={{ width: '200px' }}
          />
        </div>
      )
    } else if (
      (activity.icon_image_original_file_name !== null && fileName === null) ||
      (activity.icon_image_url === null && fileName === null) ||
      fileSize > 50000
    ) {
      iconComponent = <div />
    } else if (activity.icon_image_url !== null) {
      iconComponent = (
        <img
          src={activity.icon_image_url}
          style={{ width: '200px', height: '200px' }}
        />
      )
    }
    return iconComponent
  }

  return (
    <Modal
      width={1400}
      percentHeight={100}
      background={'rgba(36,54,70,0.48)'}
      lockBodyScroll={false}
      opacity={'.9'}
      allowInnerScroll
      allowCloseOnExternalClick={false}
    >
      <ModalContainer>
        {isLoading ? (
          <div className="loading" />
        ) : (
          <React.Fragment>
            <HeaderContainer>
              <H2>Activity Icon</H2>
              <FlexContainer>
                <MarginRight15>
                  <CancelButton
                    handleClick={closeTheModal}
                    title="Cancel"
                    width="104px"
                  />
                </MarginRight15>
                <div
                  data-for="createCourseTooltip"
                  data-tip="File size limit is 500 KB"
                >
                  <Button
                    width="104px"
                    title="Create"
                    handleClick={handleSaveActivityIcon}
                    buttonColor={colors.oeYellow}
                    borderColor={colors.oeYellow}
                    hoverBackground={colors.charcoal4}
                    hoverColor={colors.white}
                    couldSaveData={couldSaveData}
                    shouldShowTooltipAndDisableButton={true}
                  />
                  {!couldSaveData && (
                    <ReactTooltip
                      id="createCourseTooltip"
                      place="bottom"
                      effect="solid"
                      className="activityCourseTooltip"
                    />
                  )}
                </div>
              </FlexContainer>
            </HeaderContainer>
            <MainContainer>
              <div>
                {activity.icon_image_url === null ? (
                  <FlexContainer
                    style={{
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}
                  >
                    <Label htmlFor="file-input">Choose File</Label>
                    <p style={{ marginLeft: '10px' }}>
                      {fileName !== null ? fileName : 'No file chosen'}
                    </p>
                    <input
                      id="file-input"
                      className="activity-icon-input"
                      type="file"
                      onChange={onFileLoad}
                      ref={inputRef}
                      style={{ display: 'none' }}
                    />
                  </FlexContainer>
                ) : (
                  <div style={{ margin: '20px 0' }}>
                    {activity.icon_image_original_file_name}
                    <span
                      style={{
                        marginLeft: '15px',
                        marginTop: '4px',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                      onClick={handleDeleteActivityIcon}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 25 24"
                      >
                        <g
                          fill="none"
                          fillRule="evenodd"
                          stroke={isHovered ? colors.crimsonLite : colors.dove}
                          strokeWidth="3"
                          transform="translate(2 1)"
                        >
                          <path d="M0 0L22 22M22 0L0 22" />
                        </g>
                      </svg>
                    </span>
                  </div>
                )}
                {displayAvatarCropper()}
              </div>
              <HintText>
                {fileSize > 500000 && (
                  <span style={{ color: colors.crimson }}>
                    File size limit is 500 KB.
                  </span>
                )}{' '}
                For best viewing, please use a square png or jpeg image.
              </HintText>
            </MainContainer>
          </React.Fragment>
        )}
      </ModalContainer>
    </Modal>
  )
}

AddOrRemoveActivityIconModal.propTypes = {
  apiData: PropTypes.object.isRequired,
  levelId: PropTypes.string.isRequired,
  activityQuestId: PropTypes.string.isRequired,
  activity: PropTypes.object.isRequired
}

export default withApiData(AddOrRemoveActivityIconModal)



// WEBPACK FOOTER //
// ./src/components/modal/AddOrRemoveActivityIconModal.js